import { useMemo, useState } from 'react';
import { format, isToday } from 'date-fns';
import { Copy, Trash2, Plus, GripVertical } from 'lucide-react';

/**
 * ScheduleListView — employee-centric week grid.
 *
 *   ┌──────────┬─────┬─────┬─────┬─────┬─────┬─────┬─────┐
 *   │ Person   │ Mon │ Tue │ Wed │ Thu │ Fri │ Sat │ Sun │
 *   ├──────────┼─────┼─────┼─────┼─────┼─────┼─────┼─────┤
 *   │ Alex (C) │     │ ▌ X │     │ ▌ X │     │     │     │
 *   │ Bob  (P) │ ▌ Y │     │ ▌ Y │     │ ▌ Y │     │     │
 *   └──────────┴─────┴─────┴─────┴─────┴─────┴─────┴─────┘
 *
 * Each shift card is coloured by its PROJECT — so all "LC1081 Cherry Tree
 * Lodge" cards across the week visually group together regardless of who
 * is on them. This is the operational question that matters for site mgmt.
 *
 * Desktop only. Mobile users see the existing project-grid week view.
 */

const formatProjectRef = (ref) => {
  // "LUX208-DEV-10 The Avenue, Northampton"  ->  { code: "LUX208 · DEV", addr: "10 The Avenue, Northampton" }
  if (!ref) return { code: '', addr: '' };
  const parts = ref.split('-');
  if (parts.length >= 3) {
    return {
      code: `${parts[0]} · ${parts[1]}`,
      addr: parts.slice(2).join('-').trim(),
    };
  }
  const [a, b] = ref.split(' - ');
  return { code: a || ref, addr: b || '' };
};


export const ScheduleListView = ({
  days,
  schedules,
  employees,
  canManageProjects,
  projectColorMap,
  employeeColorMap,
  PROJECT_COLORS,
  EMPLOYEE_COLORS,
  draggedSchedule,
  draggedProject,
  getSchedulesForDay,
  getInitials,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onDuplicate,
  onDelete,
  onAddClick,
  onEdit,
}) => {
  // Tracks which (employeeId|'unassigned', isoDate) cell is currently a
  // drop target — used purely for visual highlight while the user drags.
  const [hoverCell, setHoverCell] = useState(null);
  // True whenever ANY drag-from-this-page is in progress (existing shift OR
  // a project from the right-rail sidebar). Drives the drop-target highlight.
  const isDragging = Boolean(draggedSchedule || draggedProject);
  // ----------------------------------------------------------------
  // Build a fast lookup: employeeId -> isoDateString -> schedule[]
  //
  // We iterate via getSchedulesForDay(day) so the lookup honours the
  // project / employee filters set on the parent (Schedule.jsx). Using the
  // raw `schedules` prop here would render cards that don't belong to the
  // currently-selected filter — see the Feb 2026 regression.
  // ----------------------------------------------------------------
  const grid = useMemo(() => {
    const map = new Map();
    const unassigned = new Map();          // dateString -> schedule[]
    const seen = new Set();                 // de-dupe across days
    for (const day of days) {
      const dayKey = format(day, 'yyyy-MM-dd');
      const daySchedules = getSchedulesForDay(day);
      for (const s of daySchedules) {
        if (seen.has(s.id)) continue;
        seen.add(s.id);
        const emps = s.employees || [];
        if (emps.length === 0) {
          unassigned.set(dayKey, [...(unassigned.get(dayKey) || []), s]);
          continue;
        }
        for (const e of emps) {
          if (!map.has(e.id)) map.set(e.id, new Map());
          const byDay = map.get(e.id);
          byDay.set(dayKey, [...(byDay.get(dayKey) || []), s]);
        }
      }
    }
    return { map, unassigned };
  }, [days, getSchedulesForDay]);

  // Per-day totals shown in the header row
  const dayTotals = useMemo(() => {
    const totals = days.map((d) => {
      const daySchedules = getSchedulesForDay(d);
      const shiftCount = daySchedules.length;
      // headcount = unique employees scheduled that day
      const empSet = new Set();
      for (const s of daySchedules) for (const e of (s.employees || [])) empSet.add(e.id);
      return { shifts: shiftCount, crew: empSet.size };
    });
    return totals;
  }, [days, getSchedulesForDay]);

  // Per-employee weekly summary
  const employeeSummary = useMemo(() => {
    const out = {};
    for (const emp of employees) {
      const byDay = grid.map.get(emp.id);
      let shifts = 0;
      if (byDay) {
        for (const arr of byDay.values()) shifts += arr.length;
      }
      out[emp.id] = { shifts };
    }
    return out;
  }, [employees, grid]);

  // ----------------------------------------------------------------
  // Shared cell renderer
  // ----------------------------------------------------------------
  const renderShiftCard = (schedule, employeeId, day) => {
    const projectColors = projectColorMap[schedule.project_id] || PROJECT_COLORS[0];
    const { code, addr } = formatProjectRef(schedule.project_ref);
    const thisShiftDragging = draggedSchedule?.id === schedule.id;
    return (
      <div
        key={`${schedule.id}-${employeeId || 'unassigned'}`}
        draggable={canManageProjects && Boolean(onDragStart)}
        onDragStart={(e) => onDragStart && onDragStart(e, schedule)}
        onDragEnd={onDragEnd}
        onDoubleClick={(e) => { e.stopPropagation(); onEdit && onEdit(schedule); }}
        title="Double-click to edit · drag to reassign"
        className={`group relative px-2 py-1.5 rounded border-l-4 ${projectColors.bg} ${projectColors.border} text-[11px] leading-tight ${canManageProjects && onDragStart ? 'cursor-grab active:cursor-grabbing' : ''} ${thisShiftDragging ? 'opacity-40' : ''}`}
        data-testid={`list-shift-${schedule.id}`}
        style={{ borderLeftColor: projectColors.borderHex || undefined }}
      >
        <div className="flex items-start gap-1">
          {canManageProjects && onDragStart && (
            <GripVertical className="w-3 h-3 text-gray-500 flex-shrink-0 mt-px" />
          )}
          <div className="min-w-0 flex-1">
            <div className={`font-semibold truncate ${projectColors.text}`}>{code}</div>
            <div className="text-gray-300 truncate">{addr}</div>
          </div>
        </div>
        {canManageProjects && (
          <div className="absolute top-0.5 right-0.5 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-[#1E2530]/80 rounded px-0.5">
            <button
              onClick={(e) => { e.stopPropagation(); onDuplicate(schedule, day); }}
              className="p-0.5 text-gray-400 hover:text-[#F5A623]"
              title="Duplicate"
            >
              <Copy className="w-3 h-3" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(schedule.id, e); }}
              className="p-0.5 text-gray-400 hover:text-red-400"
              title="Delete"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderEmptyCell = (day, employeeId) => (
    canManageProjects ? (
      <button
        onClick={() => onAddClick(day, employeeId)}
        className="w-full h-full min-h-[40px] rounded border border-dashed border-white/5 hover:border-[#F5A623]/50 hover:bg-[#F5A623]/5 flex items-center justify-center text-gray-600 hover:text-[#F5A623] transition-colors group"
        data-testid={`list-empty-${employeeId || 'unassigned'}-${format(day, 'yyyy-MM-dd')}`}
      >
        <Plus className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100" />
      </button>
    ) : <div className="min-h-[40px]" />
  );

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <div className="hidden md:block overflow-x-auto" data-testid="schedule-list-view">
      <div className="min-w-[1100px] bg-[#2A3240] rounded-lg border border-white/10 overflow-hidden">
        {/* Top day-header row */}
        <div className="grid grid-cols-[200px_repeat(7,minmax(0,1fr))] border-b border-white/10 bg-[#1E2530] sticky top-0 z-10">
          <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500 border-r border-white/10">
            Employee
          </div>
          {days.map((day, i) => {
            const today = isToday(day);
            const t = dayTotals[i];
            return (
              <div
                key={day.toISOString()}
                className={`px-2 py-2 border-r border-white/5 ${today ? 'bg-[#F5A623]/10' : ''}`}
              >
                <div className="flex items-baseline gap-2">
                  <span className={`text-[10px] uppercase ${today ? 'text-[#F5A623] font-semibold' : 'text-gray-500'}`}>
                    {format(day, 'EEE')}
                  </span>
                  <span className={`text-sm font-semibold ${today ? 'text-[#F5A623]' : 'text-white'}`}>
                    {format(day, 'd MMM')}
                  </span>
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  {t.shifts} {t.shifts === 1 ? 'shift' : 'shifts'} · {t.crew} crew
                </div>
              </div>
            );
          })}
        </div>

        {/* Unassigned shifts row (only when there are any) */}
        {Array.from(grid.unassigned.values()).some(arr => arr.length > 0) && (
          <div className="grid grid-cols-[200px_repeat(7,minmax(0,1fr))] border-b border-white/10 bg-amber-500/5">
            <div className="px-3 py-2 flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-300 text-xs font-bold">!</div>
              <div className="text-xs font-medium text-amber-300">Unassigned</div>
            </div>
            {days.map((day) => {
              const dayKey = format(day, 'yyyy-MM-dd');
              const cards = grid.unassigned.get(dayKey) || [];
              const cellKey = `unassigned|${dayKey}`;
              const isHover = hoverCell === cellKey && isDragging;
              return (
                <div
                  key={dayKey}
                  data-testid={`list-cell-unassigned-${dayKey}`}
                  className={`p-1 border-r border-white/5 space-y-1 transition-colors ${isHover ? 'bg-amber-500/20 ring-1 ring-amber-400/60' : ''}`}
                  onDragOver={(e) => {
                    // Always preventDefault so the browser doesn't reject
                    // the drop — even on the very first dragover frame
                    // before React has flushed our draggedProject state.
                    if (onDragOver) onDragOver(e);
                    if (isDragging) setHoverCell(cellKey);
                  }}
                  onDragLeave={() => setHoverCell(prev => (prev === cellKey ? null : prev))}
                  onDrop={(e) => {
                    setHoverCell(null);
                    onDrop && onDrop(e, day, null);
                  }}
                >
                  {cards.length ? cards.map(s => renderShiftCard(s, null, day)) : renderEmptyCell(day, null)}
                </div>
              );
            })}
          </div>
        )}

        {/* Employee rows */}
        {employees.map((emp) => {
          const empColor = employeeColorMap[emp.id] || EMPLOYEE_COLORS[0];
          const byDay = grid.map.get(emp.id);
          const summary = employeeSummary[emp.id];
          return (
            <div
              key={emp.id}
              className="grid grid-cols-[200px_repeat(7,minmax(0,1fr))] border-b border-white/5 hover:bg-white/[0.02]"
              data-testid={`list-emp-row-${emp.id}`}
            >
              <div className="px-3 py-2 flex items-center gap-2 border-r border-white/5">
                <div
                  className={`w-7 h-7 rounded-full ${empColor.bg} border ${empColor.border} flex items-center justify-center text-[10px] font-bold ${empColor.text} flex-shrink-0`}
                >
                  {getInitials(emp.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-white truncate" title={emp.name}>{emp.name}</div>
                  <div className="text-[10px] text-gray-500">
                    {summary.shifts} {summary.shifts === 1 ? 'shift' : 'shifts'}
                    {emp.trade && <span className="text-gray-600"> · {emp.trade}</span>}
                  </div>
                </div>
              </div>
              {days.map((day) => {
                const dayKey = format(day, 'yyyy-MM-dd');
                const cards = byDay?.get(dayKey) || [];
                const cellKey = `${emp.id}|${dayKey}`;
                const isHover = hoverCell === cellKey && isDragging;
                return (
                  <div
                    key={dayKey}
                    className={`p-1 border-r border-white/5 space-y-1 transition-colors ${isHover ? 'bg-[#F5A623]/15 ring-1 ring-[#F5A623]/60' : ''}`}
                    data-testid={`list-cell-${emp.id}-${dayKey}`}
                    onDragOver={(e) => {
                      // Always preventDefault — see note on unassigned row.
                      if (onDragOver) onDragOver(e);
                      if (isDragging) setHoverCell(cellKey);
                    }}
                    onDragLeave={() => setHoverCell(prev => (prev === cellKey ? null : prev))}
                    onDrop={(e) => {
                      setHoverCell(null);
                      onDrop && onDrop(e, day, emp.id);
                    }}
                  >
                    {cards.length
                      ? cards.map(s => renderShiftCard(s, emp.id, day))
                      : renderEmptyCell(day, emp.id)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};
